/*
  IT Assessment Test (ITAT) service — wraps /api/v1/ita.

  Mirrors controlTestingService (CAT). The UNIT under test is an IT asset or IT
  application; the TESTABLE ITEMS are the controls wired through it. A template
  scopes a set of assets/apps; rollout creates one test program per asset/app;
  the maker scores each wired control Pass/Fail/NA + effectiveness/existence; the
  checker approves.
*/

import apiClient from './apiService';

export type ItaTargetType = 'IT_ASSET' | 'IT_APPLICATION';

export interface ItaTemplate {
  id: number;
  entity_id?: number;
  ita_code: string;
  // System-generated code (ITAT-N) — null until approved.
  code?: string | null;
  name?: string;
  description?: string;
  cadence?: string;
  due_date_definition?: string;
  responsible_user_id?: number | null;
  accountable_user_id?: number | null;
  target_count?: number;
  is_active: number;
  is_approved: number;
  workflow_state?: 'draft' | 'submitted' | 'approved' | 'rejected';
  created_by?: number | null;
  created_on?: string;
  modified_by?: number | null;
  modified_on?: string;
  created_by_name?: string | null;
  submitted_by?: number | null;
  submitted_on?: string | null;
  submitted_by_name?: string | null;
  approved_by?: number | null;
  approved_on?: string | null;
  approved_by_name?: string | null;
  approval_note?: string | null;
  rejected_by?: number | null;
  rejected_on?: string | null;
  rejected_by_name?: string | null;
  rejection_reason?: string | null;
}

export interface ItaTemplateTarget {
  scope_id?: number;
  target_type: ItaTargetType;
  target_id: number;
  target_label?: string | null;
  target_code?: string | null;
  wired_control_count?: number;
}

export interface ItaTemplateCreatePayload {
  // Legacy manual code — no longer collected; system code (ITAT-N) replaces it.
  ita_code?: string;
  name?: string;
  description?: string;
  cadence?: string;
  due_date_definition?: string;
  responsible_user_id?: number | null;
  accountable_user_id?: number | null;
  targets?: { target_type: ItaTargetType; target_id: number }[];
}

export type ItaTemplateUpdatePayload = Partial<Omit<ItaTemplateCreatePayload, 'targets'>> & {
  is_active?: number;
  is_approved?: number;
};

export interface ItaScopeReplacePayload {
  targets: { target_type: ItaTargetType; target_id: number }[];
}

const BASE = '/api/v1/ita/templates';

export const itaTemplateService = {
  async list(search?: string): Promise<ItaTemplate[]> {
    const params: Record<string, string> = { limit: '200' };
    if (search) params.search = search;
    const resp = await apiClient.get(BASE, { params });
    return resp.data?.data ?? [];
  },

  async get(id: number): Promise<ItaTemplate & { targets: ItaTemplateTarget[] }> {
    const resp = await apiClient.get(`${BASE}/${id}`);
    return resp.data?.data;
  },

  async create(payload: ItaTemplateCreatePayload): Promise<ItaTemplate> {
    const resp = await apiClient.post(BASE, payload);
    return resp.data?.data;
  },

  async update(id: number, payload: ItaTemplateUpdatePayload): Promise<ItaTemplate> {
    const resp = await apiClient.put(`${BASE}/${id}`, payload);
    return resp.data?.data;
  },

  async remove(id: number): Promise<void> {
    await apiClient.delete(`${BASE}/${id}`);
  },

  async listScope(id: number): Promise<ItaTemplateTarget[]> {
    const resp = await apiClient.get(`${BASE}/${id}/scope`);
    return resp.data?.data ?? [];
  },

  async replaceScope(id: number, targets: { target_type: ItaTargetType; target_id: number }[]): Promise<void> {
    await apiClient.put(`${BASE}/${id}/scope`, { targets } as ItaScopeReplacePayload);
  },

  async rollout(templateId: number, payload: ItaRolloutPayload): Promise<{
    data: any;
    programs_created: number[];
    skipped: { target_type: ItaTargetType; target_id: number; reason: string }[];
  }> {
    const resp = await apiClient.post(`${BASE}/${templateId}/rollout`, payload);
    return resp.data;
  },

  // --- Approval workflow (template-scoped endpoints) ---
  async submit(id: number, note?: string): Promise<void> {
    try {
      await apiClient.post(`${BASE}/${id}/submit`, { note: note ?? null });
    } catch (error: any) {
      throw new Error(error.response?.data?.detail || error.response?.data?.error || 'Failed to submit template');
    }
  },

  async approve(id: number, note?: string): Promise<void> {
    try {
      await apiClient.post(`${BASE}/${id}/approve`, { note: note ?? null });
    } catch (error: any) {
      throw new Error(error.response?.data?.detail || error.response?.data?.error || 'Failed to approve template');
    }
  },

  async reject(id: number, reason: string): Promise<void> {
    try {
      await apiClient.post(`${BASE}/${id}/reject`, { reason });
    } catch (error: any) {
      throw new Error(error.response?.data?.detail || error.response?.data?.error || 'Failed to reject template');
    }
  },

  async reopen(id: number): Promise<void> {
    try {
      await apiClient.post(`${BASE}/${id}/reopen`);
    } catch (error: any) {
      throw new Error(error.response?.data?.detail || error.response?.data?.error || 'Failed to reopen template');
    }
  },

};

// =============================================================================
// ITAT Programs — rolled-out instances (one per asset/application)
// =============================================================================

export interface ItaRolloutPayload {
  program_name?: string;
  maker_id?: number | null;
  checker_id: number;
  geography?: {
    mode: 'locations' | 'subtree' | 'type';
    location_ids?: number[];
    root_location_id?: number;
    geography_type_id?: number;
  };
  start_date: string;
  end_date: string;
}

export type ItaProgramStatus =
  | 'Not-Due'
  | 'Not-Started'
  | 'In-Progress'
  | 'Complete'
  | 'Approved';

export interface ItaProgram {
  id: number;
  entity_id?: number;
  template_id?: number | null;
  program_name: string;
  target_type?: ItaTargetType | null;
  target_id?: number | null;
  target_label?: string | null;
  target_code?: string | null;
  maker_id?: number | null;
  maker_name?: string | null;
  checker_id?: number | null;
  checker_name?: string | null;
  location_id?: number | null;
  location_name?: string | null;
  start_date?: string | null;
  end_date?: string | null;
  due_date?: string | null;
  status?: ItaProgramStatus | null;
  control_count?: number;
  completed_count?: number;
  is_active?: number;
  template_code?: string | null;
  template_name?: string | null;
  template_target_count?: number;
  workflow_state?: string | null;
  rejection_reason?: string | null;
  rejected_by?: number | null;
  rejected_on?: string | null;
}

export interface ItaScoreRow {
  score_id: number;
  control_id: number;
  implementation_id?: number | null;
  result?: 'Pass' | 'Fail' | 'NA' | null;
  effectiveness_score?: number | null;
  existence_score?: number | null;
  remarks?: string | null;
  date_of_testing?: string | null;
  is_approved?: number;
  control_code?: string | null;
  control_name?: string | null;
  control_type?: string | null;
  sequence_number?: number | null;
  implementation_type?: string | null;
  impl_target_id?: number | null;
  explanation?: string | null;
}

export interface ItaProgramDetail extends ItaProgram {
  scores: ItaScoreRow[];
}

export interface ItaProgramListOpts {
  mine?: boolean;
  assigned_to_me?: boolean;
  checker_for_me?: boolean;
  status?: string;
  template_id?: number;
  search?: string;
}

const PROG_BASE = '/api/v1/ita/programs';

export const itaProgramService = {
  async list(opts?: ItaProgramListOpts): Promise<ItaProgram[]> {
    const params: Record<string, string> = { limit: '200' };
    if (opts?.mine) params.mine = 'true';
    if (opts?.assigned_to_me) params.assigned_to_me = 'true';
    if (opts?.checker_for_me) params.checker_for_me = 'true';
    if (opts?.status) params.status = opts.status;
    if (opts?.template_id) params.template_id = String(opts.template_id);
    if (opts?.search) params.search = opts.search;
    const resp = await apiClient.get(PROG_BASE, { params });
    return resp.data?.data ?? [];
  },

  async get(id: number): Promise<ItaProgramDetail> {
    const resp = await apiClient.get(`${PROG_BASE}/${id}`);
    return resp.data?.data;
  },

  async updateScore(
    programId: number,
    scoreId: number,
    payload: {
      result?: 'Pass' | 'Fail' | 'NA' | null;
      effectiveness_score?: number | null;
      existence_score?: number | null;
      remarks?: string | null;
    },
  ): Promise<void> {
    await apiClient.put(`${PROG_BASE}/${programId}/scores/${scoreId}`, payload);
  },

  async submit(programId: number): Promise<void> {
    try {
      await apiClient.post(`${PROG_BASE}/${programId}/submit`);
    } catch (error: any) {
      throw new Error(error.response?.data?.detail || error.response?.data?.error || 'Failed to submit program');
    }
  },

  async approve(programId: number): Promise<void> {
    try {
      await apiClient.post(`${PROG_BASE}/${programId}/approve`);
    } catch (error: any) {
      throw new Error(error.response?.data?.detail || error.response?.data?.error || 'Failed to approve program');
    }
  },

  async reject(programId: number, reason: string): Promise<void> {
    try {
      await apiClient.post(`${PROG_BASE}/${programId}/reject`, { reason });
    } catch (error: any) {
      throw new Error(error.response?.data?.detail || error.response?.data?.error || 'Failed to reject program');
    }
  },
};

export default itaTemplateService;
